import { supabase } from "@/integrations/supabase/client";

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export const uploadCommunityPostImage = async (file: File, userId: string): Promise<ImageUploadResult> => {
  try {
    console.log('📤 [uploadCommunityPostImage] Iniciando upload da imagem:', file.name);
    
    // Validações
    if (!file) {
      return { success: false, error: 'Nenhum arquivo fornecido' };
    }

    // Verificar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { 
        success: false, 
        error: 'Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP' 
      };
    }

    // Verificar tamanho (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { 
        success: false, 
        error: 'Arquivo muito grande. Máximo permitido: 5MB' 
      };
    }

    // Gerar nome único para o arquivo
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${userId}/${timestamp}_${randomString}.${fileExtension}`;

    console.log('📤 [uploadCommunityPostImage] Nome do arquivo:', fileName);

    // Fazer upload para o Supabase Storage
    const { data, error } = await supabase.storage
      .from('community-posts')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ [uploadCommunityPostImage] Erro no upload:', error);
      return { 
        success: false, 
        error: `Erro ao fazer upload: ${error.message}` 
      };
    }

    console.log('✅ [uploadCommunityPostImage] Upload realizado:', data);

    // Obter URL pública da imagem
    const { data: urlData } = supabase.storage
      .from('community-posts')
      .getPublicUrl(fileName);

    if (!urlData.publicUrl) {
      return { 
        success: false, 
        error: 'Erro ao obter URL da imagem' 
      };
    }

    console.log('✅ [uploadCommunityPostImage] URL gerada:', urlData.publicUrl);

    return {
      success: true,
      url: urlData.publicUrl
    };

  } catch (error: any) {
    console.error('❌ [uploadCommunityPostImage] Erro inesperado:', error);
    return { 
      success: false, 
      error: `Erro inesperado: ${error.message}` 
    };
  }
};

export const uploadDigitalContentCover = async (file: File, userId: string): Promise<ImageUploadResult> => {
  try {
    console.log('📤 [uploadDigitalContentCover] Iniciando upload da capa:', file.name);
    
    // Validações
    if (!file) {
      return { success: false, error: 'Nenhum arquivo fornecido' };
    }

    // Verificar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { 
        success: false, 
        error: 'Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP' 
      };
    }

    // Verificar tamanho (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { 
        success: false, 
        error: 'Arquivo muito grande. Máximo permitido: 5MB' 
      };
    }

    // Gerar nome único para o arquivo
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `covers/${userId}/${timestamp}_${randomString}.${fileExtension}`;

    console.log('📤 [uploadDigitalContentCover] Nome do arquivo:', fileName);

    // Fazer upload para o Supabase Storage
    const { data, error } = await supabase.storage
      .from('digital-contents')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ [uploadDigitalContentCover] Erro no upload:', error);
      return { 
        success: false, 
        error: `Erro ao fazer upload: ${error.message}` 
      };
    }

    console.log('✅ [uploadDigitalContentCover] Upload realizado:', data);

    // Obter URL pública da imagem
    const { data: urlData } = supabase.storage
      .from('digital-contents')
      .getPublicUrl(fileName);

    if (!urlData.publicUrl) {
      return { 
        success: false, 
        error: 'Erro ao obter URL da imagem' 
      };
    }

    console.log('✅ [uploadDigitalContentCover] URL gerada:', urlData.publicUrl);

    return {
      success: true,
      url: urlData.publicUrl
    };

  } catch (error: any) {
    console.error('❌ [uploadDigitalContentCover] Erro inesperado:', error);
    return { 
      success: false, 
      error: `Erro inesperado: ${error.message}` 
    };
  }
};

export const deleteCommunityPostImage = async (imageUrl: string): Promise<boolean> => {
  try {
    // Extrair o caminho do arquivo da URL
    const urlParts = imageUrl.split('/');
    const bucketIndex = urlParts.findIndex(part => part === 'community-posts');
    
    if (bucketIndex === -1) {
      console.error('❌ [deleteCommunityPostImage] URL inválida:', imageUrl);
      return false;
    }

    const filePath = urlParts.slice(bucketIndex + 1).join('/');
    
    console.log('🗑️ [deleteCommunityPostImage] Deletando arquivo:', filePath);

    const { error } = await supabase.storage
      .from('community-posts')
      .remove([filePath]);

    if (error) {
      console.error('❌ [deleteCommunityPostImage] Erro ao deletar:', error);
      return false;
    }

    console.log('✅ [deleteCommunityPostImage] Arquivo deletado com sucesso');
    return true;

  } catch (error) {
    console.error('❌ [deleteCommunityPostImage] Erro inesperado:', error);
    return false;
  }
}; 